import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { supabase } from 'supabaseClient';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

const BulkActionButton = ({ 
  filteredProfiles, 
  organizationId, 
  onSuccess, 
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [buttonText, setButtonText] = useState(`Actions (${filteredProfiles?.length || 0})`);

  useEffect(() => {
    setButtonText(`Actions (${filteredProfiles?.length || 0})`);
  }, [filteredProfiles?.length]);

  const options = [
    { 
      label: 'Accept All', 
      value: 'approve',
      icon: <CheckCircleIcon />,
      color: '#4CAF50'
    },
    { 
      label: 'Waitlist All', 
      value: 'waitlist',
      icon: <AccessTimeIcon />,
      color: '#FFC107'
    },
    { 
      label: 'Reject All', 
      value: 'reject',
      icon: <ThumbDownIcon />,
      color: '#F44336'
    }
  ];

  const getStatusFromAction = () => {
    switch (selectedAction) {
      case 'approve':
        return 'accepted';
      case 'waitlist':
        return 'waitlist';
      case 'reject':
        return 'rejected';
      default:
        return 'accepted';
    }
  };

  const getConfirmText = () => {
    const count = filteredProfiles?.length || 0;
    const option = options.find(opt => opt.value === selectedAction);
    return `${option?.label.toLowerCase()} ${count} profiles?`;
  };

  const handleBulkAction = async () => {
    if (!filteredProfiles || filteredProfiles.length === 0 || !organizationId) {
      setError('No profiles selected or organization ID missing');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setButtonText('Processing...');

    try {
      const token = await supabase.auth.getSession().then(({ data }) => data.session?.access_token);

      if (!token) {
        throw new Error('Failed to fetch authentication token');
      }

      const profileIds = filteredProfiles.map(profile => profile.id);
      const status = getStatusFromAction();

      const BASE_URL = process.env.REACT_APP_ENV === 'development'
        ? process.env.REACT_APP_BACKEND_URL_LOCAL
        : process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${BASE_URL}/org-profiles-batch-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_ids: profileIds,
          organization_id: organizationId,
          status: status
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update profiles`);
      }

      const result = await response.json();
      console.log('Bulk action result:', result);
      
      setSuccess(true);
      setShowConfirm(false);
      setSelectedAction(null);
      setButtonText('Updated Successfully!');
      
      // Reset button text after 5 seconds
      setTimeout(() => {
        setButtonText(`Bulk Actions (${filteredProfiles?.length || 0})`);
      }, 5000);
      
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      setError(err.message || 'An error occurred during bulk action');
      setButtonText(`Bulk Actions (${filteredProfiles?.length || 0})`);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    if (!disabled && filteredProfiles?.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value) => {
    setSelectedAction(value);
    setAnchorEl(null);
    setShowConfirm(true);
  };

  return (
    <Container>
      <ActionButton
        onClick={handleClick}
        disabled={disabled || loading || filteredProfiles?.length === 0}
        endIcon={<ArrowDropDownIcon />}
      >
        {buttonText}
      </ActionButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {options.map(option => (
          <StyledMenuItem 
            key={option.value}
            onClick={() => handleMenuItemClick(option.value)}
            $color={option.color}
          >
            <span className="menu-item-content">
              {option.icon}
              {option.label}
            </span>
          </StyledMenuItem>
        ))}
      </Menu>

      {showConfirm && (
        <ConfirmDialog>
          <ConfirmText>{getConfirmText()}</ConfirmText>
          <ButtonGroup>
            <CancelButton onClick={() => setShowConfirm(false)} disabled={loading}>
              Cancel
            </CancelButton>
            <ConfirmButton 
              onClick={handleBulkAction} 
              disabled={loading}
              $color={options.find(opt => opt.value === selectedAction)?.color}
            >
              {loading ? 'Processing...' : 'Confirm'}
            </ConfirmButton>
          </ButtonGroup>
        </ConfirmDialog>
      )}
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: auto;
  margin-right: -15px;
`;

const ActionButton = muiStyled(Button)`
  padding: 5px 10px;
  text-transform: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  background-color: rebeccapurple;
  color: white;
  border: none;
  &:hover {
    background-color: rebeccapurple;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

const StyledMenuItem = muiStyled(MenuItem)`
  padding: 8px 15px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
    display: flex;
    justify-content: flex-end;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  .MuiSvgIcon-root {
    font-size: 16px;
    margin-right: 5px;
    margin-top: 1px;
    color: ${props => props.$color};
    color: black;
  }
  
  .menu-item-content {
    display: flex;
    align-items: center;
  }
`;

const ConfirmDialog = styled.div`
  position: absolute;
  top: -5px;
  right: 0;
  background: white;
  border-radius: 4px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
  padding: 12px 20px;
  z-index: 10;
`;

const ConfirmText = styled.div`
  font-size: 14px;
  margin-bottom: 12px;
  color: #333;
  text-transform: capitalize;
  font-weight: 900;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 5px;
`;

const CancelButton = styled.button`
  padding: 6px 12px;
  border-radius: 3px;
  background-color: #f1f1f1;
  border: none;
  cursor: pointer;
  font-size: 13px;
  
  &:hover {
    background-color: #e1e1e1;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ConfirmButton = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-size: 13px;
  background-color: rebeccapurple;
  color: white;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ErrorMessage = styled.div`
  color: #F44336;
  font-size: 12px;
  margin-top: 8px;
`;

export default BulkActionButton; 